import { type DateTime } from "luxon";
import { SchoolModel } from "model";
import { Colors } from "swing-components";

import { TimesTableRowType, type TimesTableArrayType } from "~components/RequestForm/hooks";
import { SubScheduleDay } from "~components/RequestForm/utils";

import { DayField } from "./DayField";
import { MultiDateField } from "./MultiDateField";

export type RequestFormDateField = {
  /** The current datetime in the Schools timezone */
  now: DateTime;
  /** The School being edited */
  school: SchoolModel;
  /** The intervals for the Request */
  intervals: TimesTableArrayType;

  /** The original end date of the request */
  originalEndDate?: DateTime;

  /*** MultiDateField ***/
  onDateChange: (params: { startDate?: DateTime; endDate?: DateTime }) => void;

  /*** DayField ***/
  /** Triggered when clicking on "Add Another Day" button. */
  onAddAnotherDay: () => void;

  /*** MultiDateTimeField ***/
  /** Multi Day **/
  /*
   * TODO: Currently the logic for time change is in the component, but we'll
   * want to move it upwards to the Request form.
   */
  onDateTimeChange: (newInterval: Array<TimesTableRowType>) => void;
  /** Long Term **/
  multiDayAdjustmentNotes: string;
  onMultiDayAdjustmentNotesChange: (notes: string) => void;
  /** Whether the field is disabled because the request is waitlisted or filled */
  isWaitlistedOrFilledReq?: boolean;
  /** The sub schedule for the request */
  subSchedule?: Array<SubScheduleDay>;
  /** Whether the field is disabled because the request is waitlisted or filled */
  isExtendedRequestEnabled?: boolean;
};

export function RequestFormDateField(props: RequestFormDateField) {
  /*** Props ***/
  // MultiDateField
  const { now, school, intervals, onDateChange } = props;
  // DayField
  const { onAddAnotherDay } = props;
  // MultiDateTimeField props
  const {
    // Multi Day
    onDateTimeChange,
    // Long Term
    multiDayAdjustmentNotes,
    onMultiDayAdjustmentNotesChange,
    isWaitlistedOrFilledReq,
    originalEndDate,
    subSchedule,
    isExtendedRequestEnabled,
  } = props;

  /*** Hooks ***/
  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Step #2.1: Select Dates */}
      <MultiDateField
        now={now}
        originalEndDate={originalEndDate}
        schoolHours={school.hours}
        schoolClosures={school.closures}
        start={getIntervalStartDateTime(intervals)}
        end={getIntervalEndDateTime(intervals)}
        onChange={onDateChange}
        isWaitlistedOrFilledReq={isWaitlistedOrFilledReq}
        isExtendedRequestEnabled={isExtendedRequestEnabled}
        subSchedule={subSchedule}
      />

      {/* Divider: TODO: Do we have this */}
      <div css={{ height: 1, background: Colors.slate200 }} />

      <DayField
        intervals={intervals}
        school={{ id: school.id, timezone: school.tzString }}
        originalEndDate={originalEndDate}
        additionalEdits={multiDayAdjustmentNotes}
        onIntervalChange={onDateTimeChange}
        onAddAnotherDay={onAddAnotherDay}
        onAdditionalEditsChange={onMultiDayAdjustmentNotesChange}
        isWaitlistedOrFilledReq={isWaitlistedOrFilledReq}
        isExtendedRequestEnabled={isExtendedRequestEnabled}
        subSchedule={subSchedule}
      />
    </div>
  );
}

/***** Helper Functions *****/
function getIntervalStartDateTime(intervals: TimesTableArrayType) {
  return intervals.filter((interval) => interval.isEnabled).at(0)!.startTime;
}

function getIntervalEndDateTime(intervals: TimesTableArrayType) {
  return intervals.filter((interval) => interval.isEnabled).at(-1)!.endTime;
}
